import { useEffect, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import type { ArtifactType, ConvertAndCopyFormat } from "~/services/artifacts";
import { defaultFormatFor, formatOptionsFor } from "./formats";
import { useArtifactActions } from "./useArtifactActions";

type Stage = "select" | "downloading" | "error";

interface DownloadDialogProps {
  open: boolean;
  onClose: () => void;
  artifactId: string;
  artifactType: ArtifactType;
}

// Modal flow for downloading an artifact. Mirrors the assistant-ui
// `DownloadDialog` shape — pick a format, kick off the download, show
// a spinner while the convert-and-copy Lambda runs, recover from
// errors. Replaces the old split-button caret menu.
export const DownloadDialog = ({
  open,
  onClose,
  artifactId,
  artifactType,
}: DownloadDialogProps) => {
  const actions = useArtifactActions(artifactId);
  const options = formatOptionsFor(artifactType);
  const [stage, setStage] = useState<Stage>("select");
  const [format, setFormat] = useState<ConvertAndCopyFormat>(defaultFormatFor(artifactType));
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setStage("select");
      setFormat(defaultFormatFor(artifactType));
      setError(undefined);
    }
  }, [open, artifactType]);

  const handleDownload = async () => {
    setStage("downloading");
    const result = await actions.download(format);
    if (result.ok) {
      onClose();
    } else {
      setError(result.error);
      setStage("error");
    }
  };

  const handleRetry = () => {
    setError(undefined);
    setStage("select");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        {stage === "select" && (
          <>
            <DialogHeader>
              <DialogTitle>Download artifact</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="mb-4 text-sm text-gray-600">Choose a format for your download:</p>
              <RadioGroup
                value={format}
                onValueChange={(v) => setFormat(v as ConvertAndCopyFormat)}
              >
                {options.map((opt) => (
                  <RadioGroupItem
                    key={opt.value}
                    value={opt.value}
                    label={opt.label}
                    description={opt.description}
                    variant="card"
                    className="w-full"
                  />
                ))}
              </RadioGroup>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleDownload}>Download</Button>
            </div>
          </>
        )}

        {stage === "downloading" && (
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <Loader2 className="size-10 animate-spin text-primary" />
            <p className="text-base font-medium">Preparing download…</p>
            <p className="text-sm text-gray-500">This may take a moment for large files.</p>
          </div>
        )}

        {stage === "error" && (
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <div className="rounded-full bg-red-100 p-5">
              <AlertCircle className="size-8 text-red-600" />
            </div>
            <div className="space-y-1 text-center">
              <p className="text-lg font-bold">Unable to download artifact</p>
              <p className="text-sm text-gray-600">
                {error || "Something went wrong while downloading your file."}
              </p>
            </div>
            <div className="mt-2 flex flex-col items-center gap-1">
              <Button onClick={handleRetry} className="px-6">
                Retry
              </Button>
              <Button variant="link" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
